import { CellContext } from '@tanstack/react-table';
import { getTableMeta, PurchaseOrderRow } from '../../helpers';
import styles from './CreatedByAndFileUpload.module.css';
import { SkeletonizedValue } from '@round/ui-kit';
import { Button, assets } from '@round/whitelabel-ui';
import { useUpdatePurchaseOrder } from '@round/creatorbase-helpers/api-hooks';
import { file, network } from '@round/utils';
import { showNotification } from 'helpers';
import cn from 'classnames';
import { useState } from 'react';
import { isEmptyObject } from 'utility/utility';

const { File: FileIcon, Download: DownloadIcon, Upload: UploadIcon, Trash: TrashIcon } = assets;

type Context = CellContext<PurchaseOrderRow, any>;

const CreatedByAndFileUpload = ({ table, row: { original } }: Context) => {
    const { isLoading, getIsTeamLoading, getIsUserLoading } = getTableMeta(table);
    const isDataLoading =
        (isLoading || getIsTeamLoading(original) || getIsUserLoading(original)) && isEmptyObject(original);
    const hasFileUploaded = !!original.file_upload;
    const {
        update,
        isLoading: isPurchaseOrderUpdating,
        helpers: { getErrorMessage },
    } = useUpdatePurchaseOrder(original.id);

    const handleFileUpload = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.pdf';

        fileInput.addEventListener('change', () => {
            update(
                { file_upload: fileInput.files?.[0] },
                {
                    onSuccess: () => {
                        showNotification('File uploaded', 'info');
                    },
                    onError: (error) => {
                        showNotification(getErrorMessage(error), 'error');
                    },
                }
            );
        });

        fileInput.click();
    };

    const handleDelete = () => {
        update(
            { file_upload: '' },
            {
                onSuccess: () => {
                    showNotification('File deleted', 'info');
                },
                onError: (error) => {
                    showNotification(getErrorMessage(error), 'error');
                },
            }
        );
    };

    const [isFileDownloading, setIsFileDownloading] = useState(false);
    const handleDownload = async () => {
        if (!original.file_upload) {
            return;
        }

        try {
            setIsFileDownloading(true);
            const response = await network.fetchFile(original.file_upload);
            if (response instanceof File) {
                file.downloadFile(response);
                return;
            }

            showNotification('Could not download file', 'error');
        } catch (e) {
            showNotification('Could not download file', 'error');
        } finally {
            setIsFileDownloading(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.fileSection}>
                <div className={styles.fileIconContainer}>
                    <FileIcon className={styles.icon} />

                    <div
                        className={cn(styles.indicator, {
                            [styles.hasFile]: hasFileUploaded,
                            [styles.noFile]: !hasFileUploaded,
                        })}
                    />
                </div>

                <div className={styles.buttonsContainer}>
                    {hasFileUploaded ? (
                        <Button
                            className={styles.action}
                            appearance="ghost"
                            type="button"
                            isLoading={isFileDownloading}
                            onClick={handleDownload}
                        >
                            <DownloadIcon className={styles.icon} />
                        </Button>
                    ) : (
                        <Button
                            className={styles.action}
                            appearance="ghost"
                            type="button"
                            isLoading={isPurchaseOrderUpdating}
                            onClick={handleFileUpload}
                        >
                            <UploadIcon className={styles.icon} />
                        </Button>
                    )}

                    {hasFileUploaded && (
                        <Button
                            className={styles.action}
                            appearance="ghost"
                            type="button"
                            isLoading={isPurchaseOrderUpdating}
                            onClick={handleDelete}
                        >
                            <TrashIcon className={cn(styles.icon, styles.delete)} />
                        </Button>
                    )}
                </div>
            </div>

            <p className={styles.team}>
                <SkeletonizedValue isInitialized={!isDataLoading} width="10rem">
                    {original.team?.name}
                </SkeletonizedValue>
            </p>
            <p className={styles.createdBy}>
                <SkeletonizedValue isInitialized={!isDataLoading} width="7rem">
                    {original.createdBy?.name}
                </SkeletonizedValue>
            </p>
        </div>
    );
};

export default CreatedByAndFileUpload;
